import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { Load } from "components/load";
import PageError from "components/page-error";
import { ErrorBoundary } from "@/components/error-boundary";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  History
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetPropagationDetailsQuery } from "@/services/campaign-propagation";
import { cn } from "@/lib/utils";
import { StoreLogsDialog } from "./components/store-logs-dialog";
import { useState } from "react";
import { PropagationKpiCards } from "@/components/propagation/propagation-kpi-cards";
import { PropagationStatusBadge } from "@/components/propagation/propagation-status-badge";
import { usePropagationFilters } from "@/hooks/usePropagationFilters";
import { usePropagationRetry } from "@/hooks/usePropagationRetry";
import { CampaignType, PROPAGATION_STATUS } from "@/utils/campaign-types";
import type { IPropagationStore } from "@/@types/campaign-propagation";

export function CampaignPropagationDetails() {
  const { type, code } = useParams<{ type: CampaignType; code: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Quando o usuário chega aqui a partir do Comparativo, o "Voltar" retorna ao
  // Comparativo (preserva o contexto de análise). Acesso direto pela lista de
  // propagação mantém o comportamento de voltar para a lista.
  const fromComparison = Boolean(
    (location.state as { fromComparison?: boolean } | null)?.fromComparison,
  );
  const handleBack = () => {
    if (fromComparison && type && code) {
      navigate(
        `/planejamento-comercial/comparativo-retaguarda/${type}/${code}`,
      );
      return;
    }
    navigate("/planejamento-comercial/auditoria-propagacao");
  };

  // Estado para modal de logs
  const [selectedStore, setSelectedStore] = useState<IPropagationStore | null>(null);

  // Hooks customizados
  const filters = usePropagationFilters();
  const { refetch } = useGetPropagationDetailsQuery({
    type: type!,
    code: parseInt(code!),
    page: filters.page,
    perPage: filters.perPage,
    ...(filters.statusFilter && { status: filters.statusFilter }),
    ...(filters.storeCodeFilter && { storeCode: parseInt(filters.storeCodeFilter) }),
  });

  const retry = usePropagationRetry({
    type: type!,
    code: parseInt(code!),
    onSuccess: refetch,
  });

  // Fetch data
  const { data: response, isLoading, error } = useGetPropagationDetailsQuery({
    type: type!,
    code: parseInt(code!),
    page: filters.page,
    perPage: filters.perPage,
    ...(filters.statusFilter && { status: filters.statusFilter }),
    ...(filters.storeCodeFilter && { storeCode: parseInt(filters.storeCodeFilter) }),
  });

  const { header, stores = [], pagination } = response || { pagination: { page: 1, limit: 50, total: 0, totalPages: 1 } };

  if (isLoading) return <Load />;

  const errorStatus = (error as { status?: number } | undefined)?.status;
  const notFound = errorStatus === 404;

  // 404 = campanha sem registro de propagação. Não é erro de servidor: mostra
  // estado amigável mantendo o "Voltar", em vez do PageError 500 (tela cheia).
  if (error && !notFound) {
    return <PageError status={500} message={"ERRO INTERNO NO SERVIDOR"} />;
  }

  if (notFound) {
    return (
      <ErrorBoundary>
        <PageLayout>
          <PageTitle
            title={`Auditoria de Propagação - ${type} #${code}`}
            description="Detalhes de propagação por loja"
          />
          <ContentContainer>
            <div className="space-y-4">
              <Button size="sm" variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {fromComparison ? "Voltar para Comparativo" : "Voltar para Lista"}
              </Button>

              <div
                role="alert"
                className="flex items-start gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-4 dark:border-yellow-900/50 dark:bg-yellow-950/30"
              >
                <AlertCircle className="mt-0.5 h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <div className="font-semibold text-yellow-800 dark:text-yellow-200">
                    Campanha não encontrada
                  </div>
                  <div className="text-sm text-yellow-700 dark:text-yellow-300">
                    Não há registro de propagação para {type} #{code}. Verifique
                    se a campanha já foi propagada para as lojas.
                  </div>
                </div>
              </div>
            </div>
          </ContentContainer>
        </PageLayout>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <PageLayout>
        <PageTitle
          title={`Auditoria de Propagação - ${type} #${code}`}
          description="Detalhes de propagação por loja"
        />
        <ContentContainer>
        <div className="space-y-4">
          {/* Botão Voltar */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleBack}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {fromComparison ? "Voltar para Comparativo" : "Voltar para Lista"}
          </Button>

          {/* KPI Cards - Componentizado */}
          {header && <PropagationKpiCards header={header} />}

          {/* Alerta de falhas */}
          {header && header.failed_count > 0 && (
            <div
              role="alert"
              className="flex items-start justify-between gap-4 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-red-100 p-1 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  <AlertCircle className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-semibold text-red-800 dark:text-red-200">Atenção</div>
                  <div className="text-sm text-red-700 dark:text-red-300">
                    {header.failed_count} loja(s) com falha na propagação
                  </div>
                </div>
              </div>
              <div className="shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={retry.retryAll}
                  disabled={retry.isRetryingAll}
                >
                  <RefreshCw className={cn("mr-2 h-4 w-4", retry.isRetryingAll && "animate-spin")} />
                  Reprocessar Todas
                </Button>
              </div>
            </div>
          )}

          {/* Toolbar - Filtros usando constantes */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={filters.statusFilter || "ALL"}
                onValueChange={(value) => {
                  filters.setStatusFilter(value === "ALL" ? null : value as any);
                }}
              >
                <SelectTrigger className="w-[180px] h-8">
                  <SelectValue placeholder="Filtrar por Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos os Status</SelectItem>
                  <SelectItem value={PROPAGATION_STATUS.PENDING}>Pendente</SelectItem>
                  <SelectItem value={PROPAGATION_STATUS.IN_PROGRESS}>Em Progresso</SelectItem>
                  <SelectItem value={PROPAGATION_STATUS.SUCCESS}>Sucesso</SelectItem>
                  <SelectItem value={PROPAGATION_STATUS.FAILED}>Falha</SelectItem>
                </SelectContent>
              </Select>

              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={() => refetch()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Atualizar
              </Button>
            </div>
          </div>

          {/* Data Table - Stores */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Loja</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tentativas</TableHead>
                  <TableHead>Última Tentativa</TableHead>
                  <TableHead>Erro</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      Nenhuma loja encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  stores.map((store: IPropagationStore) => (
                    <TableRow key={store.id}>
                      <TableCell className="font-medium">
                        {store.storeCode} - {store.storeName}
                      </TableCell>
                      <TableCell>
                        <PropagationStatusBadge status={store.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{store.retryCount || store.attemptCount}/3</span>
                          {store.retryCount > 1 && (
                            <AlertCircle className="h-4 w-4 text-yellow-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(store.lastAttemptAt || store.lastAttempt)
                          ? new Date(store.lastAttemptAt || store.lastAttempt!).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit"
                            })
                          : "-"}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                        {store.errorMessage ? store.errorMessage : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {store.status === PROPAGATION_STATUS.FAILED && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => retry.retryStore(store.id)}
                              disabled={retry.isRetryingStore}
                            >
                              <RefreshCw className={cn("mr-1 h-3 w-3", retry.isRetryingStore && "animate-spin")} />
                              Retry
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedStore(store)}
                          >
                            <History className="mr-1 h-3 w-3" />
                            Logs
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação usando hook */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Página {pagination.page} de {pagination.totalPages} ({pagination.total} lojas)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => filters.setPage(1)}
                  disabled={pagination.page === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => filters.setPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm font-medium">
                  {pagination.page} / {pagination.totalPages}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => filters.setPage(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => filters.setPage(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </ContentContainer>

      {/* Modal de Logs */}
      <StoreLogsDialog
        open={!!selectedStore}
        onOpenChange={(open) => !open && setSelectedStore(null)}
        storeCode={selectedStore?.storeCode || 0}
        storeName={selectedStore?.storeName || ""}
        logs={(() => {
          if (!selectedStore) return [];

          const logs = [];

          // Adicionar logs de erros (se houver)
          if (selectedStore.attemptErrors && selectedStore.attemptErrors.length > 0) {
            selectedStore.attemptErrors.forEach((error, index) => {
              logs.push({
                id: index + 1,
                timestamp: error.timestamp,
                status: PROPAGATION_STATUS.FAILED,
                attemptNumber: error.attempt,
                errorMessage: error.error,
              });
            });
          }

          // Se status atual é SUCCESS, adicionar log de sucesso
          if (selectedStore.status === PROPAGATION_STATUS.SUCCESS) {
            const successTimestamp = selectedStore.lastAttemptAt || selectedStore.lastAttempt || selectedStore.updatedAt;
            logs.push({
              id: logs.length + 1,
              timestamp: successTimestamp,
              status: PROPAGATION_STATUS.SUCCESS,
              attemptNumber: selectedStore.retryCount || selectedStore.attemptCount || 1,
              errorMessage: null,
            });
          }

          // Se status atual é PENDING ou IN_PROGRESS, mostrar o status atual
          if (selectedStore.status === PROPAGATION_STATUS.PENDING || selectedStore.status === PROPAGATION_STATUS.IN_PROGRESS) {
            logs.push({
              id: logs.length + 1,
              timestamp: selectedStore.createdAt || new Date().toISOString(),
              status: selectedStore.status,
              attemptNumber: selectedStore.retryCount || selectedStore.attemptCount || 0,
              errorMessage: null,
            });
          }

          // Ordenar do mais recente para o mais antigo
          return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        })()}
      />
    </PageLayout>
    </ErrorBoundary>
  );
}
